import React from "react";
import { useTranslation } from "react-i18next";
import { motion, useReducedMotion } from "framer-motion";
import { FiCheck, FiMail, FiSend } from "react-icons/fi";
import { Button } from "../../ui/Button";
import { useToast } from "../../context/ToastContext";
import { useNewsletterSubscribe } from "../../hooks/useNewsletterSubscribe";
import * as S from "./NewsletterSignup.styled";

/**
 * Newsletter ("Buletini") sign-up section. Responsive at every width, unlike the Farms page's
 * desktop-only variant, because the homepage renders one tree for both breakpoints.
 */
const NewsletterSignup = ({ source = "home" }) => {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();
  const { success: toastSuccess, error: toastError } = useToast();
  const { email, setEmail, submit, submitting } = useNewsletterSubscribe({
    source,
    onSuccess: toastSuccess,
    onError: toastError,
  });

  const perks = [
    t("farms.subscribe.perk1", {
      defaultValue: "Weekly fresh drops straight to your inbox",
    }),
    t("farms.subscribe.perk2", {
      defaultValue: "Early access to seasonal baskets",
    }),
    t("farms.subscribe.perk3", {
      defaultValue: "Unsubscribe any time — no spam",
    }),
  ];

  const emailLabel = t("farms.subscribe.email_placeholder", {
    defaultValue: "Enter your email",
  });

  return (
    <S.Section>
      <S.Panel
        as={motion.div}
        initial={{ opacity: 0, y: reduceMotion ? 0 : 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: reduceMotion ? 0 : 0.6, ease: [0.22, 1, 0.36, 1] }}
      >
        <S.Copy>
          <S.Kicker>
            <FiMail aria-hidden="true" />
            {t("farms.subscribe.kicker", { defaultValue: "Newsletter" })}
          </S.Kicker>
          <S.Title>
            {t("farms.subscribe.section_title", { defaultValue: "Stay Updated" })}
          </S.Title>
          <S.Body>
            {t("farms.subscribe.subscribe_text", {
              defaultValue:
                "Subscribe to our newsletter for the latest updates on fresh produce!",
            })}
          </S.Body>
          <S.Perks>
            {perks.map((perk) => (
              <S.Perk key={perk}>
                <FiCheck size={16} aria-hidden="true" />
                {perk}
              </S.Perk>
            ))}
          </S.Perks>
        </S.Copy>

        <S.Form onSubmit={submit}>
          <S.Field>
            <FiMail size={18} aria-hidden="true" />
            <S.Input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder={emailLabel}
              aria-label={emailLabel}
            />
          </S.Field>
          <Button
            type="submit"
            variant="primary"
            iconRight={<FiSend aria-hidden="true" />}
            fullWidth
            disabled={submitting}
          >
            {submitting
              ? t("farms.subscribe.subscribing", { defaultValue: "Subscribing…" })
              : t("farms.subscribe.subscribe_button", { defaultValue: "Subscribe" })}
          </Button>
          <S.Note>
            {t("farms.subscribe.privacy", {
              defaultValue: "We respect your privacy. Read more in our terms.",
            })}
          </S.Note>
        </S.Form>
      </S.Panel>
    </S.Section>
  );
};

export default NewsletterSignup;
